import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { concat, find, get, isEmpty, map } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 15;
const TEXT_OFFSET = 5;
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'radial-line-chart';

class RadialLineChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      colorRange,
      dataNamed,
      dataNumeric,
      globalProperties,
      height,
      isCard,
      selectedElements,
      weight,
      width
    } = this.props;

    const textSize = get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE);

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height])
      .style('overflow', 'visible');

    const data = map(dataNamed, (namedValue, index) => ({
      name: namedValue,
      value: dataNumeric[index]
    }));

    const radiusToUse = Math.min(width, height);

    const outerRadius = radiusToUse / 2 - CARD_MARGIN;

    const innerRadius = radiusToUse / TEXT_OFFSET;

    const x = d3
      .scaleBand()
      .domain(map(data, d => d.name))
      .range([0, 2 * Math.PI])
      .padding(0);

    const y = d3
      .scaleLinear()
      .domain([
        Math.min(
          0,
          d3.min(data, d => Number(d.value))
        ),
        d3.max(data, d => Number(d.value))
      ])
      .nice()
      .range([innerRadius, outerRadius]);

    const line = d3
      .lineRadial()
      .curve(d3.curveLinearClosed)
      .angle(d => x(d.name));

    const xAxis = g => {
      g.attr('text-anchor', 'middle')
        .attr('font-family', 'sans-serif')
        .attr('font-size', textSize)
        .selectAll('g')
        .data(y.ticks(5).reverse())
        .join('g')
        .attr('fill', 'none')
        .append('circle')
        .attr('stroke', '#000')
        .attr('stroke-opacity', 0.2)
        .attr('r', y);
      if (!isCard && selectedElements.Text && selectedElements.Axis) {
        g.selectAll('g')
          .data(concat(y.ticks(5).reverse(), y.ticks(5).reverse()))
          .join('g')
          .append('text')
          .attr('y', (d, i, array) => {
            if (i / array.length >= 0.5) return y(d);
            return -y(d);
          })
          .attr('dy', '0.35em')
          .attr('stroke', '#fff')
          .attr('stroke-width', 5)
          .text(d => `${d.toFixed(0)}`);
        g.selectAll('g')
          .data(concat(y.ticks(5).reverse(), y.ticks(5).reverse()))
          .join('g')
          .append('text')
          .attr('y', (d, i, array) => {
            if (i / array.length >= 0.5) return y(d);
            return -y(d);
          })
          .attr('dy', '0.35em')
          .attr('stroke', '#000')
          .attr('stroke-width', 1)
          .text(d => `${d.toFixed(0)}`);
      }
    };

    const yAxis = g => {
      g.attr('font-family', 'sans-serif')
        .attr('font-size', textSize)
        .selectAll('g')
        .data(data)
        .join('g')
        .append('path')
        .attr('stroke', '#000')
        .attr('stroke-opacity', (d, i) =>
          (2 * i) % data.length === 0 && selectedElements.Axis ? 1 : 0.2
        )
        .attr(
          'd',
          d =>
            `M${d3.pointRadial(x(d.name), innerRadius)}L${d3.pointRadial(x(d.name), outerRadius)}`
        );
      if (!isCard && selectedElements.Text) {
        g.selectAll('g')
          .data(data)
          .join('g')
          .append('g')
          .attr('text-anchor', d => (x(d.name) < Math.PI ? 'end' : 'start'))
          .attr(
            'transform',
            d =>
              `rotate(${
                x(d.name) < Math.PI
                  ? (x(d.name) * 180) / Math.PI - 90
                  : ((x(d.name) + Math.PI) * 180) / Math.PI - 90
              }) translate(${
                x(d.name) < Math.PI ? innerRadius - TEXT_OFFSET : TEXT_OFFSET - innerRadius
              }, ${TEXT_OFFSET})`
          )
          .append('text')
          .text(d => d.name)
          .attr('opacity', 0.6);
      }
    };

    svg
      .append('g')
      .attr('class', `${this.id}`)
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', colorRange)
      .attr('stroke-opacity', alpha)
      .attr('stroke-width', weight)
      .attr('d', line.radius(d => y(d.value))(data))
      .attr('transform', `translate(${width / 2},${height / 2})`);

    svg
      .append('g')
      .attr('class', `${this.id}`)
      .attr('transform', `translate(${width / 2},${height / 2})`)
      .call(yAxis);

    svg
      .append('g')
      .attr('class', `${this.id}`)
      .attr('transform', `translate(${width / 2},${height / 2})`)
      .call(xAxis);

    svg.selectAll('text').attr('font-size', textSize);
  };
}

RadialLineChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'],
  dataNumeric: [1, 1.5, 2, 3, 6, 10, 7, 6.5, 6, 5, 3, 0],
  colorRange: 'steelblue',
  alpha: 0.9,
  weight: 2,
  globalProperties: [],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  isCard: false
};
RadialLineChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNamed: PropTypes.array,
  dataNumeric: PropTypes.array,
  colorRange: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  alpha: PropTypes.number,
  weight: PropTypes.number,
  globalProperties: PropTypes.array,
  selectedElements: PropTypes.object,
  isCard: PropTypes.bool
};

export default RadialLineChart;
